/* eslint-disable no-nested-ternary */
/* eslint-disable no-console */
/**
=========================================================
* ClientReservationsTimeline.jsx
* - Month view only
* - Search button (no auto search)
* - dateClick opens modal to add reservation
* - eventClick opens modal to edit reservation
* - timeZone from project.timeZone for display
=========================================================
*/

import { useMemo, useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

// @mui
import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import MenuItem from "@mui/material/MenuItem";

// components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CardTitle from "components/CardTitle";
import Calendar from "components/Calendar";

import { useTranslation } from "react-i18next";
import enLocale from "@fullcalendar/core/locales/en-gb";
import esLocale from "@fullcalendar/core/locales/es";

import { reduxForm, formValueSelector, change as changeFormValue } from "redux-form";
import ClientForm from "layouts/clients/components/clientForm";
import { upsertClient } from "store/actions/clientsActions";
import ClientReservationForm from "layouts/clients/components/clientReservationsForm";
import MDInput from "components/MDInput";
import {
  upsertClientReservation,
  removeClientReservation,
} from "store/actions/clientReservationsActions";
import { newAlertMessage } from "store/actions/globalAlertActions";
import {
  addHoursInTZMs,
  buildAppliedLabel,
  buildTimelineEventContent,
  buildTimelineEvents,
  getApartmentsNames,
  getReservationsList,
  startOfDayInTZMs,
} from "../clientReservationsUtils";

const selectSx = {
  "& .MuiInputBase-root": {
    height: 44,
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    padding: "10.5px 14px",
  },
};

function ClientReservationsTimeline({
  projects,
  apartments,
  clients,
  clientReservations,
  projectId,
  apartmentId,
  change,
  actUpsertClientReservation,
  actRemoveClientReservation,
  actUpsertClient,
  actChangeClientReservationClient,
  actNewAlertMessage,
}) {
  const { t, i18n } = useTranslation();

  const [open, setOpen] = useState(false);
  const [clientErrorMessage, setClientErrorMessage] = useState("");

  const [actionType, setActionType] = useState("");
  const [uids, setUids] = useState([]);

  const [fixedStartDate, setFixedStartDate] = useState(null);
  const [fixedEndDate, setFixedEndDate] = useState(null);

  const [appliedProjectId, setAppliedProjectId] = useState("");
  const [appliedApartmentId, setAppliedApartmentId] = useState("");

  const [creatingClientFromReservation, setCreatingClientFromReservation] = useState(false);

  const clientReservationsList = useMemo(
    () => getReservationsList(clientReservations),
    [JSON.stringify(clientReservations)]
  );

  const appliedProject = appliedProjectId ? projects?.[appliedProjectId] : null;
  const appliedApartment = appliedApartmentId ? apartments?.[appliedApartmentId] : null;

  const appliedTimeZone = appliedProject?.timeZone || "UTC";

  const appliedLabel = useMemo(
    () =>
      buildAppliedLabel({
        appliedProjectId,
        appliedApartmentId,
        appliedProject,
        appliedApartment,
      }),
    [appliedProjectId, appliedApartmentId, appliedProject, appliedApartment]
  );

  const getProjectsNames = () =>
    Object.entries(projects || {})
      .map(([uid, project]) => ({ uid, name: project?.name || uid }))
      .sort((a, b) =>
        (a?.name || "").toString().localeCompare((b?.name || "").toString(), undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );

  const projectOptions = getProjectsNames();
  const hasSingleProject = projectOptions.length === 1;

  useEffect(() => {
    if (hasSingleProject) {
      const singleProjectId = projectOptions[0]?.uid || "";
      if (singleProjectId && projectId !== singleProjectId) {
        change("projectId", singleProjectId);
        change("apartmentId", "");
      }
    }
  }, [hasSingleProject, projectOptions, projectId, change]);

  const getApartmentsNamesForProject = () => getApartmentsNames(projects, projectId, apartments);
  const apartmentOptions = getApartmentsNamesForProject();

  const showCalendar = !!appliedProjectId && !!appliedApartmentId;

  const events = useMemo(() => {
    if (!showCalendar) return [];

    return buildTimelineEvents({
      reservations: clientReservationsList,
      appliedProjectId,
      appliedApartmentId,
      clients,
    });
  }, [
    showCalendar,
    JSON.stringify(clientReservationsList),
    JSON.stringify(clientReservations),
    appliedProjectId,
    appliedApartmentId,
    clients,
  ]);

  const renderEventContent = (arg) => {
    const title = arg.event?.title || "";
    const isActive = !!arg.event?.extendedProps?.isActive;
    const start = arg.event?.start;
    const end = arg.event?.end;
    return buildTimelineEventContent({
      title,
      isActive,
      start,
      end,
      timeZone: appliedTimeZone,
    });
  };

  const onDateClick = (info) => {
    setCreatingClientFromReservation(false);
    setClientErrorMessage("");

    if (!showCalendar) {
      setClientErrorMessage("select_project_and_apartment");
      return;
    }

    const dayStartUtcMs = startOfDayInTZMs(info.date, appliedTimeZone);
    const startMs = addHoursInTZMs(dayStartUtcMs, 12, appliedTimeZone);
    const endMs = addHoursInTZMs(dayStartUtcMs, 14, appliedTimeZone);

    setFixedStartDate(startMs);
    setFixedEndDate(endMs);
    setActionType("add");
    setUids([]);
    setOpen(true);
  };

  const openEditFromEvent = (clickInfo) => {
    setCreatingClientFromReservation(false);
    setClientErrorMessage("");
    const uid = clickInfo?.event?.id;
    if (!uid) return;

    setActionType("edit");
    setUids([uid]);
    setOpen(true);
  };

  const handleDeleteCurrent = () => {
    if (actionType !== "edit" || uids.length !== 1) return;

    const clientReservation = clientReservations.find((res) => res?.uid === uids[0]);
    const clientId = clientReservation?.clientId;

    actRemoveClientReservation({ clientId, uid: uids[0] }, (resp) => {
      const { ok } = resp || {};

      if (!ok) {
        actNewAlertMessage({
          type: "error",
          title: "clientReservation",
          content: t("errorRemovingClientReservation"),
        });
        return;
      }

      actNewAlertMessage({
        type: "success",
        title: "clientReservation",
        content: t("clientReservationRemovedSuccessfully"),
      });
    });
    if (setOpen) setOpen(false);
    if (setClientErrorMessage) setClientErrorMessage("");
    if (setActionType) setActionType("");
    if (setUids) setUids([]);
  };

  const handleCreateClientFromReservation = () => {
    setCreatingClientFromReservation(true);
  };

  const submitClientFromReservation = (data) => {
    actUpsertClient(data, (resp) => {
      const { ok, client } = resp || {};
      const { uid } = client || {};

      if (!ok || !uid) {
        actNewAlertMessage({
          type: "error",
          title: "client",
          content: "errorCreatingClient",
        });
        return;
      }

      actChangeClientReservationClient(uid);
      setCreatingClientFromReservation(false);

      actNewAlertMessage({
        type: "success",
        title: "client",
        content: t("clientCreatedSuccessfully"),
      });
    });
  };

  const handleSubmit = (data) => {
    const startMs = data.startDate?.valueOf ? data.startDate.valueOf() : data.startDate;
    const endMs = data.endDate?.valueOf ? data.endDate.valueOf() : data.endDate;

    actUpsertClientReservation(
      {
        ...data,
        startDate: startMs,
        endDate: endMs,
      },
      (resp) => {
        const { ok } = resp || {};
        if (!ok) {
          if (actionType === "add") {
            actNewAlertMessage({
              type: "error",
              title: "clientReservation",
              content: t("errorCreatingClientReservation"),
            });
            return;
          }
          if (actionType === "edit") {
            actNewAlertMessage({
              type: "error",
              title: "clientReservation",
              content: t("errorUpdatingClientReservation"),
            });
            return;
          }
        }

        if (setOpen) setOpen(false);
        if (setClientErrorMessage) setClientErrorMessage("");
        if (setActionType) setActionType("");
        if (setUids) setUids([]);

        if (actionType === "add") {
          actNewAlertMessage({
            type: "success",
            title: "clientReservation",
            content: t("clientReservationCreatedSuccessfully"),
          });
        }
        if (actionType === "edit") {
          actNewAlertMessage({
            type: "success",
            title: "clientReservation",
            content: t("clientReservationUpdatedSuccessfully"),
          });
        }
      }
    );
  };

  const onSearch = () => {
    setClientErrorMessage("");

    if (!projectId || !apartmentId) {
      setAppliedProjectId("");
      setAppliedApartmentId("");
      setClientErrorMessage("select_project_and_apartment");
      return;
    }

    setAppliedProjectId(projectId);
    setAppliedApartmentId(apartmentId);
  };

  return (
    <Card>
      <CardTitle title={t("clientReservationsTimeline")} icon="calendar_month" bgColor="info" />

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setClientErrorMessage("");
          setActionType("");
          setUids([]);
          setCreatingClientFromReservation(false);
        }}
        style={{ overflow: "scroll" }}
      >
        <MDBox
          mt={3}
          xs={12}
          sm={8}
          style={{
            position: "absolute",
            width: "65vw",
            left: "50%",
            transform: "translate(-50%, 0%)",
          }}
        >
          <Card>
            <MDBox p={2}>
              <MDBox display={creatingClientFromReservation ? "none" : "block"}>
                <ClientReservationForm
                  onSubmit={handleSubmit}
                  actionType={actionType}
                  uids={uids}
                  errorMessage={clientErrorMessage}
                  fixedProjectId={actionType === "add" ? appliedProjectId : ""}
                  fixedApartmentId={actionType === "add" ? appliedApartmentId : ""}
                  fixedStartDate={actionType === "add" ? fixedStartDate : null}
                  fixedEndDate={actionType === "add" ? fixedEndDate : null}
                  disableProjectSelector={actionType === "add"}
                  disableApartmentSelector={actionType === "add"}
                  showDeleteButton={actionType === "edit"}
                  onDelete={handleDeleteCurrent}
                  onCreateClient={handleCreateClientFromReservation}
                />
              </MDBox>

              {creatingClientFromReservation && (
                <ClientForm onSubmit={submitClientFromReservation} actionType="add" uids={[]} />
              )}
            </MDBox>
          </Card>
        </MDBox>
      </Modal>

      {/* Filters + Buscar */}
      <MDBox px={2} pt={2}>
        <MDBox display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
          <MDBox width="300px">
            <MDInput
              select
              fullWidth
              label={t("project")}
              value={projectId || ""}
              onChange={(event) => {
                change("projectId", event.target.value);
                change("apartmentId", "");
              }}
              InputLabelProps={{
                shrink: true,
              }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (selected === "" && !hasSingleProject) {
                    return t("selectProject");
                  }

                  const selectedProject = projectOptions.find(
                    (project) => project?.uid === selected
                  );

                  return selectedProject?.name || projectOptions[0]?.name || selected;
                },
              }}
              sx={selectSx}
            >
              {!hasSingleProject && (
                <MenuItem value="">
                  <em>{t("selectProject")}</em>
                </MenuItem>
              )}

              {projectOptions.map((project) => (
                <MenuItem key={project.uid} value={project.uid}>
                  {project.name}
                </MenuItem>
              ))}
            </MDInput>
          </MDBox>

          <MDBox width="300px">
            <MDInput
              select
              fullWidth
              label={t("apartment")}
              value={apartmentId || ""}
              onChange={(event) => {
                change("apartmentId", event.target.value);
              }}
              InputLabelProps={{
                shrink: true,
              }}
              disabled={!projectId}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!projectId || selected === "") {
                    return t("selectApartmentFirst");
                  }

                  const selectedApartment = apartmentOptions.find(
                    (apartment) => apartment?.uid === selected
                  );

                  return selectedApartment?.name || selected;
                },
              }}
              sx={selectSx}
            >
              <MenuItem value="">
                <em>{t("selectApartmentFirst")}</em>
              </MenuItem>

              {apartmentOptions.map((apartment) => (
                <MenuItem key={apartment.uid} value={apartment.uid}>
                  {apartment.name}
                </MenuItem>
              ))}
            </MDInput>
          </MDBox>

          <MDBox width="160px">
            <MDButton
              fullWidth
              variant="gradient"
              color="info"
              onClick={onSearch}
              disabled={!projectId || !apartmentId}
            >
              {t("customSearch") || t("select") || "Buscar"}
            </MDButton>
          </MDBox>
        </MDBox>

        {!!clientErrorMessage && (
          <MDBox mt={1}>
            <MDTypography variant="body2" color="error">
              {t(clientErrorMessage)}
            </MDTypography>
          </MDBox>
        )}
      </MDBox>

      {!showCalendar ? (
        <MDBox px={2} pb={3} pt={3}>
          <MDTypography variant="body2" color="text">
            {t("select_project_and_apartment")}
          </MDTypography>
        </MDBox>
      ) : (
        <MDBox
          px={2}
          pb={2}
          pt={2}
          sx={{
            "& .fc .fc-daygrid-day-frame": { minHeight: 120 },
            "& .fc .fc-daygrid-day-events": { minHeight: 72 },
            "& .fc .fc-daygrid-event": { paddingTop: 4, paddingBottom: 4 },
            "& .event-info": { backgroundImage: "none !important" },
          }}
        >
          <MDBox display="flex" justifyContent="center" mb={1}>
            <CardTitle title={appliedLabel} bgColor="info" />
          </MDBox>

          <Calendar
            headerToolbar={{
              start: "today prev,next",
              center: "title",
              end: "",
            }}
            initialView="dayGridMonth"
            initialDate={Date.now()}
            events={events}
            eventContent={renderEventContent}
            locale={i18n.language === "es" ? esLocale : enLocale}
            timeZone={appliedTimeZone}
            dateClick={onDateClick}
            eventClick={openEditFromEvent}
            selectable={false}
            allDaySlot={false}
            eventDisplay="block"
            dayMaxEventRows={4}
            contentHeight={1800}
            height={1800}
          />
        </MDBox>
      )}
    </Card>
  );
}

ClientReservationsTimeline.defaultProps = {
  projects: {},
  apartments: {},
  clients: {},
  clientReservations: [],
  projectId: "",
  apartmentId: "",
};

ClientReservationsTimeline.propTypes = {
  change: PropTypes.func.isRequired,
  projects: PropTypes.object,
  apartments: PropTypes.object,
  clients: PropTypes.object,
  clientReservations: PropTypes.array,
  projectId: PropTypes.string,
  apartmentId: PropTypes.string,
  actUpsertClientReservation: PropTypes.func.isRequired,
  actRemoveClientReservation: PropTypes.func.isRequired,
  actUpsertClient: PropTypes.func.isRequired,
  actChangeClientReservationClient: PropTypes.func.isRequired,
  actNewAlertMessage: PropTypes.func.isRequired,
};

const selector = formValueSelector("ClientReservationsTimelineForm");

const mapStateToProps = (state) => {
  const { projectsReducer, apartmentsReducer, clientsReducer, mainUserReducer } = state;

  const clientReservationsObj = state?.clientReservationsReducer?.clientReservations || {};
  const allClientReservations = Object.values(clientReservationsObj).flatMap((item) =>
    Object.values(item || {})
  );
  const allProjects = projectsReducer || {};
  const isProjectManager = mainUserReducer?.isProjectManager || false;
  const userProjects = mainUserReducer?.projects || {};
  let clientReservations = allClientReservations;
  let projects = allProjects;

  if (isProjectManager) {
    clientReservations = allClientReservations.filter(
      (reservation) => userProjects?.[reservation?.projectId]
    );
    projects = Object.fromEntries(
      Object.entries(allProjects).filter(([projectId]) => userProjects?.[projectId])
    );
  }

  return {
    projects,
    apartments: apartmentsReducer,
    clients: clientsReducer,
    clientReservations,
    projectId: selector(state, "projectId"),
    apartmentId: selector(state, "apartmentId"),
  };
};

const mapDispatchToProps = (dispatch) => ({
  actUpsertClientReservation: (clientReservationData, cb) => {
    dispatch(upsertClientReservation({ clientReservationData, cb }));
  },

  actRemoveClientReservation: ({ clientId, uid }, cb) => {
    dispatch(removeClientReservation({ clientId, uid, cb }));
  },

  actUpsertClient: (clientData, cb) => {
    dispatch(
      upsertClient({
        clientData,
        cb,
      })
    );
  },

  actChangeClientReservationClient: (clientId) => {
    dispatch(changeFormValue("ClientReservationForm", "clientId", clientId));
  },

  actNewAlertMessage: (message) => {
    dispatch(newAlertMessage(message));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: "ClientReservationsTimelineForm",
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    destroyOnUnmount: false,
    touchOnBlur: true,
    touchOnChange: true,
  })(ClientReservationsTimeline)
);
